import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, CircleNotch, Sparkle, ArrowRight, Heart } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { TERMS_AND_CONDITIONS_TEXT, TOS_VERSION } from "@/lib/legal";

/**
 * Public signup — creates a new workspace, provisions the owner user, and
 * assigns a 14-day free Trial plan. On success the user is dropped straight
 * into their new tenant's dashboard.
 *
 * Slug availability is checked live (debounced) against
 * `GET /api/auth/slug-available` so users never submit a form that will
 * fail on the server.
 */
export default function SignupPage() {
  const { signup } = useAuth();
  const { brand } = useBranding();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preferredTier = params.get("tier");  // ?tier=professional from pricing

  const [form, setForm] = useState({
    org_name: "",
    org_slug: "",
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    password: "",
  });
  const [slugStatus, setSlugStatus] = useState({ state: "idle", reason: "" });
  const [loading, setLoading] = useState(false);
  // iter-67k (Area 2) — T&C acceptance gate.
  const [tosOpen, setTosOpen] = useState(false);
  const [tosAgreed, setTosAgreed] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-derive slug from org name until user manually edits the slug.
  const [slugDirty, setSlugDirty] = useState(false);
  useEffect(() => {
    if (!slugDirty) {
      const s = form.org_name.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .slice(0, 32);
      setForm(f => ({ ...f, org_slug: s }));
    }
  }, [form.org_name, slugDirty]);

  const checkSlug = useCallback(async (s) => {
    if (!s || s.length < 3) { setSlugStatus({ state: "idle", reason: "" }); return; }
    setSlugStatus({ state: "checking", reason: "" });
    try {
      const { data } = await api.get(`/auth/slug-available?slug=${encodeURIComponent(s)}`);
      setSlugStatus({ state: data.available ? "ok" : "bad", reason: data.reason });
    } catch (_e) {
      setSlugStatus({ state: "bad", reason: "invalid_format" });
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => checkSlug(form.org_slug), 350);
    return () => clearTimeout(t);
  }, [form.org_slug, checkSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (slugStatus.state !== "ok") {
      toast.error("Pick an available workspace slug first");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    // iter-67k (Area 2) — every fresh workspace must go through the
    // Terms & Conditions modal. Only when the user ticks the box do
    // we actually POST /auth/signup.
    setTosOpen(true);
  };

  const confirmTosAndSignup = async () => {
    if (!tosAgreed) return;
    setLoading(true);
    try {
      const res = await signup({
        org_name: form.org_name.trim(),
        org_slug: form.org_slug.trim().toLowerCase(),
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim().toLowerCase(),
        owner_phone: form.owner_phone.trim() || null,
        password: form.password,
        tos_version: TOS_VERSION,
        tos_accepted_at: new Date().toISOString(),
      });
      toast.success(`Welcome to ${res.org.name}! Your 14-day trial has started.`);
      setTosOpen(false);
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const slugFeedback = (() => {
    if (slugStatus.state === "checking") return { icon: CircleNotch, cls: "text-muted-foreground animate-spin", text: "Checking…" };
    if (slugStatus.state === "ok") return { icon: CheckCircle, cls: "text-emerald-600", text: "Available" };
    if (slugStatus.state === "bad") {
      const map = { taken: "Already taken", reserved: "Reserved word", invalid_format: "3–32 chars: lowercase, numbers, hyphen" };
      return { icon: XCircle, cls: "text-red-600", text: map[slugStatus.reason] || "Not available" };
    }
    return null;
  })();

  return (
    <div className="min-h-screen grid lg:grid-cols-5" data-testid="signup-page">
      {/* Hero */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/33812025/pexels-photo-33812025.jpeg')",
            backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "luminosity",
          }} />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary/85 to-primary/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-primary-foreground text-primary flex items-center justify-center font-display font-bold text-xl">
              {(brand.brand_name || "T").charAt(0)}
            </div>
            <div>
              <div className="font-display font-bold text-2xl tracking-tight">{brand.brand_name}</div>
              <div className="overline text-primary-foreground/70">Therapy Center ERP</div>
            </div>
          </div>
          <div className="max-w-md space-y-6">
            <h1 className="font-display text-5xl font-bold tracking-tight leading-[1.05]">
              Start your <span className="italic">14-day</span> free trial.
            </h1>
            <p className="text-primary-foreground/85 text-base leading-relaxed">
              No credit card required. Onboard your center in minutes — students, therapists, attendance, billing.
              Upgrade when you&apos;re ready, cancel any time.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4">
              {[
                { Icon: Sparkle, label: "14 days free" },
                { Icon: Heart, label: "No card upfront" },
                { Icon: CheckCircle, label: "Cancel anytime" },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex flex-col gap-2 items-start">
                  <Icon size={22} weight="duotone" />
                  <div className="text-xs text-primary-foreground/85">{label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-primary-foreground/60">
            <Link to="/pricing" className="hover:text-primary-foreground/90">See pricing →</Link>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="lg:col-span-2 flex items-center justify-center p-6 lg:p-10">
        <div className="w-full max-w-sm space-y-5">
          <div>
            <div className="flex items-center gap-2">
              <div className="overline">Create your workspace</div>
              {preferredTier && (
                <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary text-[10px]">
                  {preferredTier}
                </Badge>
              )}
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Get started free.</h2>
            <p className="text-muted-foreground text-sm mt-1">
              You&apos;ll be the workspace Owner. You can invite staff after signup.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-3.5">
            <div className="space-y-1.5">
              <Label>Workspace name</Label>
              <Input
                required autoFocus
                value={form.org_name}
                onChange={(e) => update("org_name", e.target.value)}
                placeholder="e.g. Sunshine Therapy Center"
                data-testid="signup-org-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Workspace URL</Label>
              <div className="flex items-center rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring">
                <span className="pl-3 pr-1 text-xs text-muted-foreground select-none whitespace-nowrap">/t/</span>
                <Input
                  required
                  value={form.org_slug}
                  onChange={(e) => { setSlugDirty(true); update("org_slug", e.target.value.toLowerCase()); }}
                  placeholder="sunshine-therapy"
                  className="border-0 shadow-none focus-visible:ring-0 pl-0"
                  data-testid="signup-org-slug"
                />
                {slugFeedback && (
                  <div className={`pr-3 text-[11px] flex items-center gap-1 ${slugFeedback.cls}`}>
                    <slugFeedback.icon size={13} weight="duotone" />
                    <span data-testid="slug-status">{slugFeedback.text}</span>
                  </div>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">Lowercase, 3–32 chars. This is how your team signs in.</p>
            </div>

            <div className="pt-2 border-t border-border/60" />

            <div className="space-y-1.5">
              <Label>Your name</Label>
              <Input required value={form.owner_name} onChange={(e) => update("owner_name", e.target.value)} data-testid="signup-owner-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input required type="email" value={form.owner_email} onChange={(e) => update("owner_email", e.target.value)} data-testid="signup-owner-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={form.owner_phone} onChange={(e) => update("owner_phone", e.target.value)} placeholder="+91 …" data-testid="signup-owner-phone" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <PasswordInput required minLength={8} value={form.password} onChange={(e) => update("password", e.target.value)} placeholder="8+ characters" data-testid="signup-password" />
            </div>

            <Button type="submit" disabled={loading || slugStatus.state !== "ok"} className="w-full h-11 gap-2" data-testid="signup-submit">
              {loading ? "Creating workspace…" : <>Start free trial <ArrowRight size={16} /></>}
            </Button>
          </form>

          <p className="text-[11px] text-muted-foreground text-center">
            Already have a workspace? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </div>
      </div>

      {/* iter-67k (Area 2) — Terms & Conditions acceptance modal. */}
      <Dialog open={tosOpen} onOpenChange={(o) => { setTosOpen(o); if (!o) setTosAgreed(false); }}>
        <DialogContent className="max-w-2xl" data-testid="tos-dialog">
          <DialogHeader>
            <DialogTitle>Terms &amp; Conditions</DialogTitle>
          </DialogHeader>
          <div
            data-testid="tos-scroll"
            className="max-h-[50vh] overflow-y-auto rounded-md border border-border bg-muted/30 p-4 text-[13px] leading-relaxed whitespace-pre-wrap font-sans"
          >
            {TERMS_AND_CONDITIONS_TEXT}
          </div>
          <label className="flex items-start gap-2 pt-2 text-sm">
            <Checkbox
              checked={tosAgreed}
              onCheckedChange={(v) => setTosAgreed(!!v)}
              data-testid="tos-checkbox"
              className="mt-0.5"
            />
            <span>I have read and agree to the Terms and Conditions.</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTosOpen(false); setTosAgreed(false); }} data-testid="tos-cancel">Cancel</Button>
            <Button
              onClick={confirmTosAndSignup}
              disabled={!tosAgreed || loading}
              data-testid="tos-accept-continue"
            >
              {loading ? "Creating workspace…" : "Create workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
